/**
 * Composable per Cost Centers autocomplete con loading e error handling centralizzati
 */

import { ref, readonly } from 'vue';
import { useODataBuilder } from '../useODataBuilder';
import { useLoadingState } from '../useLoadingState';
import { useNotification } from '../useNotification';
import { useStaleSafeRequest } from '../useStaleSafeRequest';
import type { BaseAutocompleteComposableOptions } from './types';
import { getCostCentersAutocomplete } from '@/lib/autocompletes/costCenters';
import { CostCenterAutocompleteResult } from '~/lib/models/costCenter';
import { BaseAutocompleteOptions } from '~/lib/autocompletes';

export function useCostCentersAutocomplete(options: BaseAutocompleteComposableOptions = {}) {
	const costCenters = ref<CostCenterAutocompleteResult[]>([]);

	const { buildOdataQuery } = useODataBuilder();
	const withLoadingFn = options.withLoading || useLoadingState().withLoading;
	const { error } = useNotification();
	const { requestToken } = useStaleSafeRequest();

	async function searchCostCenters(options: BaseAutocompleteOptions = {}): Promise<void> {
		const token = requestToken();
		await withLoadingFn(async () => {
			try {
				const result = await getCostCentersAutocomplete(buildOdataQuery, options);
				if (token.isLatest()) {
					costCenters.value = result;
				}
			} catch (err) {
				console.error('❌ ERROR useCostCentersAutocomplete.searchCostCenters:', err);
				error(
					'Errore durante il caricamento dei centri di costo! Ricaricare la pagina e riprovare.',
				);
				if (token.isLatest()) {
					costCenters.value = [];
				}
			}
		}, 'searchCostCenters');
	}

	function clearCostCenters(): void {
		costCenters.value = [];
	}

	return {
		costCenters: readonly(costCenters),
		searchCostCenters,
		clearCostCenters,
	};
}
